import React, { useEffect, useMemo, useRef, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Upload, Map as MapIcon, Layers, Search } from "lucide-react";
import { toast } from "sonner";

const ASSET_TYPES = [
  { value: "road", label: "Road" },
  { value: "osr", label: "OSR" },
  { value: "footpath", label: "Footpath" },
  { value: "sipcot_po_office", label: "SIPCOT PO office" },
  { value: "dumping_yard", label: "Dumping yard" },
  { value: "other", label: "Other" },
];

const typeLabel = (t) => ASSET_TYPES.find((x) => x.value === t)?.label || t;

const sortByCode = (a, b) => {
  const ai = parseInt(String(a.code || "").replace(/^0+/, "") || "0", 10);
  const bi = parseInt(String(b.code || "").replace(/^0+/, "") || "0", 10);
  return ai - bi;
};

export default function ParkAssetsPage() {
  const [parks, setParks] = useState([]);
  const [parkId, setParkId] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Add / Edit dialog
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ asset_type: "road", name: "", length_km: "", phase: "", notes: "", active: true });

  // Import dialog
  const [importOpen, setImportOpen] = useState(false);
  const fileInputRef = useRef(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importReport, setImportReport] = useState(null);

  useEffect(() => {
    api.get("/parks").then((r) => {
      const sorted = [...(r.data || [])].sort(sortByCode);
      setParks(sorted);
      if (sorted[0]) setParkId(sorted[0].id);
    });
  }, []);

  const load = async () => {
    if (!parkId) return;
    setLoading(true);
    try {
      const params = { park_id: parkId };
      if (typeFilter !== "all") params.asset_type = typeFilter;
      const { data } = await api.get("/admin/park-assets", { params });
      setItems(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load assets");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [parkId, typeFilter]);

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return items;
    return items.filter((a) =>
      (a.name || "").toLowerCase().includes(q) ||
      (a.phase || "").toLowerCase().includes(q) ||
      (a.notes || "").toLowerCase().includes(q),
    );
  }, [items, searchText]);

  const counts = useMemo(() => {
    const out = { total: items.length };
    for (const t of ASSET_TYPES) out[t.value] = 0;
    for (const a of items) out[a.asset_type] = (out[a.asset_type] || 0) + 1;
    return out;
  }, [items]);

  const openCreate = () => {
    setEditing(null);
    setForm({ asset_type: "road", name: "", length_km: "", phase: "", notes: "", active: true });
    setOpen(true);
  };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      asset_type: a.asset_type || "road",
      name: a.name || "",
      length_km: a.length_km == null ? "" : String(a.length_km),
      phase: a.phase || "",
      notes: a.notes || "",
      active: a.active !== false,
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    const payload = {
      park_id: parkId,
      asset_type: form.asset_type,
      name: form.name.trim(),
      length_km: form.length_km === "" ? null : Number(form.length_km),
      phase: form.phase.trim() || null,
      notes: form.notes.trim() || null,
      active: !!form.active,
    };
    if (payload.length_km !== null && Number.isNaN(payload.length_km)) {
      return toast.error("Length must be a number (km)");
    }
    try {
      if (editing) {
        await api.put(`/admin/park-assets/${editing.id}`, payload);
        toast.success("Asset updated");
      } else {
        await api.post("/admin/park-assets", payload);
        toast.success("Asset added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete asset "${a.name}"?`)) return;
    try {
      await api.delete(`/admin/park-assets/${a.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const toggleActive = async (a) => {
    try {
      await api.put(`/admin/park-assets/${a.id}`, { active: !a.active });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const onImportFile = async (file) => {
    if (!file) return;
    setImportBusy(true);
    setImportReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/park-assets/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportReport(data);
      toast.success(`Imported: ${data.inserted} new · ${data.updated} updated`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImportBusy(false);
    }
  };

  const downloadTemplate = () => {
    const csv = "park_code,asset_type,name,length_km,phase,notes,active\n09,road,Phase II - 1st main road,3.369,Phase II,,true\n09,osr,OSR Plot A,,,,true\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "park-assets-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const selectedPark = parks.find((p) => p.id === parkId);

  return (
    <AppShell
      title="Park Assets"
      subtitle="Master list of roads, OSR plots, footpaths and other assets per industrial park"
      action={
        <div className="flex flex-wrap items-center gap-2">
          <Select value={parkId} onValueChange={setParkId}>
            <SelectTrigger className="w-[240px]" data-testid="assets-park-select">
              <SelectValue placeholder="Pick a park" />
            </SelectTrigger>
            <SelectContent>
              {parks.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.code} — {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="assets-import-btn">
            <Upload className="mr-2 h-4 w-4" /> Import
          </Button>
          <Button onClick={openCreate} data-testid="assets-add-btn">
            <Plus className="mr-2 h-4 w-4" /> Add asset
          </Button>
        </div>
      }
    >
      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-7" data-testid="assets-kpis">
        <Card className="border-amber-100">
          <CardContent className="p-3">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Total</div>
            <div className="font-display text-2xl font-semibold text-amber-700">{counts.total}</div>
          </CardContent>
        </Card>
        {ASSET_TYPES.map((t) => (
          <Card key={t.value}>
            <CardContent className="p-3">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{t.label}</div>
              <div className="font-display text-2xl font-semibold" data-testid={`assets-count-${t.value}`}>{counts[t.value] || 0}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <div className="min-w-[180px]">
            <Label className="text-xs">Type filter</Label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger data-testid="assets-type-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {ASSET_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grow min-w-[220px]">
            <Label className="text-xs">Search</Label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Name, phase or notes…"
                className="pl-8"
                data-testid="assets-search"
              />
            </div>
          </div>
          {selectedPark && (
            <div className="text-xs text-muted-foreground">
              <MapIcon className="inline h-3.5 w-3.5 -mt-0.5 mr-1" />
              {selectedPark.code} — {selectedPark.name}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="p-6 text-sm text-muted-foreground">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground" data-testid="assets-empty">
              <Layers className="mx-auto mb-2 h-6 w-6 text-amber-500/70" />
              No assets found for this filter. Click <span className="font-medium text-foreground">Add asset</span> or <span className="font-medium text-foreground">Import</span> to seed.
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-card sticky top-0 z-10">
                <tr className="border-b">
                  <th className="px-3 py-2 text-left font-medium">Name</th>
                  <th className="px-2 py-2 text-left font-medium">Type</th>
                  <th className="px-2 py-2 text-left font-medium">Phase</th>
                  <th className="px-2 py-2 text-right font-medium">Size</th>
                  <th className="px-2 py-2 text-center font-medium">Active</th>
                  <th className="px-2 py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => (
                  <tr key={a.id} className="border-b last:border-0 hover:bg-amber-50/40" data-testid={`assets-row-${a.id}`}>
                    <td className="px-3 py-2 font-medium">{a.name}</td>
                    <td className="px-2 py-2">
                      <Badge variant="outline" className="text-[11px]">{typeLabel(a.asset_type)}</Badge>
                    </td>
                    <td className="px-2 py-2 text-muted-foreground">{a.phase || "—"}</td>
                    <td className="px-2 py-2 text-right tabular-nums">
                      {a.length_km != null ? (
                        <span>
                          {a.length_km.toFixed(a.asset_type === "osr" ? 2 : 3)}
                          <span className="ml-1 text-[11px] text-muted-foreground">
                            {a.asset_type === "osr" ? "acres" : "km"}
                          </span>
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <Switch
                        checked={!!a.active}
                        onCheckedChange={() => toggleActive(a)}
                        data-testid={`assets-toggle-${a.id}`}
                      />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(a)} data-testid={`assets-edit-${a.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => remove(a)} data-testid={`assets-delete-${a.id}`}>
                        <Trash2 className="h-4 w-4 text-rose-600" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit asset" : "Add asset"}</DialogTitle>
            <DialogDescription>
              {selectedPark ? `${selectedPark.code} — ${selectedPark.name}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Asset type</Label>
              <Select value={form.asset_type} onValueChange={(v) => setForm({ ...form, asset_type: v })}>
                <SelectTrigger data-testid="assets-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ASSET_TYPES.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Phase II - 1st main road - 3.369 kms"
                data-testid="assets-form-name"
              />
            </div>
            <div>
              <Label className="text-xs">
                {form.asset_type === "osr" ? "Extent (acres)" : "Length (km)"}
              </Label>
              <Input
                value={form.length_km}
                onChange={(e) => setForm({ ...form, length_km: e.target.value })}
                placeholder={form.asset_type === "osr" ? "0.00" : "0.000"}
                inputMode="decimal"
                data-testid="assets-form-length"
              />
            </div>
            <div>
              <Label className="text-xs">Phase</Label>
              <Input
                value={form.phase}
                onChange={(e) => setForm({ ...form, phase: e.target.value })}
                placeholder="Phase I / II"
                data-testid="assets-form-phase"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                data-testid="assets-form-notes"
              />
            </div>
            <div className="col-span-2 flex items-center justify-between">
              <Label className="text-xs">Active</Label>
              <Switch
                checked={!!form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })}
                data-testid="assets-form-active"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} data-testid="assets-form-save">{editing ? "Save changes" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) setImportReport(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Import park assets</DialogTitle>
            <DialogDescription>
              Upload an Excel (.xlsx) or CSV file. Required columns: <code className="font-mono">park_code</code> (or
              <code className="font-mono"> park_id</code>),
              <code className="font-mono"> asset_type</code>, <code className="font-mono">name</code>.
              Optional: <code className="font-mono">length_km</code>, <code className="font-mono">phase</code>,
              <code className="font-mono"> notes</code>, <code className="font-mono">active</code>.
              Existing rows (same park + type + name) are updated; new rows are inserted.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <Button variant="outline" onClick={downloadTemplate} data-testid="assets-import-template">
              Download CSV template
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.csv"
              className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-amber-100 file:px-3 file:py-2 file:text-amber-900 hover:file:bg-amber-200"
              onChange={(e) => onImportFile(e.target.files?.[0])}
              data-testid="assets-import-file"
              disabled={importBusy}
            />
            {importBusy && <div className="text-xs text-muted-foreground">Importing… please wait.</div>}
            {importReport && (
              <Card className="border-amber-100">
                <CardContent className="p-3 text-xs">
                  <div><span className="font-medium">Inserted:</span> {importReport.inserted}</div>
                  <div><span className="font-medium">Updated:</span> {importReport.updated}</div>
                  <div><span className="font-medium">Total rows:</span> {importReport.total_rows}</div>
                  {Array.isArray(importReport.errors) && importReport.errors.length > 0 && (
                    <div className="mt-2">
                      <div className="font-medium text-rose-700">Errors ({importReport.errors.length}):</div>
                      <ul className="ml-4 list-disc text-rose-700">
                        {importReport.errors.slice(0, 10).map((e, i) => (<li key={i}>{e}</li>))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
